import type { JsonObject } from '@seen/core/json'
import type { JSONContent } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { Transaction } from '@tiptap/pm/state'
import type { Editor } from '@tiptap/react'
import { suggestionInsertNodes } from '#src/components/rich-text-editor/suggestionContentExtensions.ts'
import { contractForNode } from '#src/components/rich-text-editor/suggestionContracts.ts'

// The stamp identity of a custom node an accept inserted: `${suggestionId}:${ordinal}`.
// The ordinal is a deterministic per-suggestion index (not a PM-random node id),
// so a double-insert cannot also double-create — the create keys on a stable
// identity (M4 A3). This — not a document position, which collab edits invalidate
// across awaits — is what the receipt, the conditional create, the attr patch,
// and compensation all key on (design §O.4 R3).
export function stampNodeId(suggestionId: string, ordinal: number): string {
  return `${suggestionId}:${ordinal}`
}

function belongsTo(acceptId: string, suggestionId: string): boolean {
  return acceptId === suggestionId || acceptId.startsWith(`${suggestionId}:`)
}

/*
 * Does this suggestion own any stamped node in the live doc? The entry check
 * reads this SYNCHRONOUS signal first (R3): the receipt write is async, so a
 * double-click lands in the insert→receipt window where the in-doc stamp is the
 * only current signal. Reads the client-local Yjs doc, always current for the
 * acting client.
 */
export function hasSuggestionStamp(doc: PMNode, suggestionId: string): boolean {
  let found = false
  doc.descendants((node) => {
    if (found) {
      return false
    }
    const acceptId = node.attrs?.acceptId
    if (typeof acceptId === 'string' && belongsTo(acceptId, suggestionId)) {
      found = true
    }
    return !found
  })
  return found
}

export interface StampedNode {
  pos: number
  node: PMNode
  nodeId: string
}

/* Every stamped custom node this suggestion owns in the live doc. */
export function collectStampedNodes(
  doc: PMNode,
  suggestionId: string
): StampedNode[] {
  const out: StampedNode[] = []
  doc.descendants((node, pos) => {
    const acceptId = node.attrs?.acceptId
    if (typeof acceptId === 'string' && belongsTo(acceptId, suggestionId)) {
      out.push({ pos, node, nodeId: acceptId })
    }
    return true
  })
  return out
}

export interface StampedContent {
  // The top-level nodes to insert, parsed through the shared preview parse
  // (suggestionInsertNodes) and carrying the stamp attrs. Parsed here rather
  // than left as HTML for insertContentAt to parse, because that parse keeps
  // inter-block whitespace the preview drops.
  content: JSONContent[]
  // The stamp identities assigned, recorded on the receipt (R1) and iterated by
  // the side-effect post-pass (R2). Empty for a plain-content accept.
  nodeIds: string[]
}

/*
 * Stamp the custom nodes in `content` that carry a side effect — a registered
 * `applyOnAccept` — with this suggestion's id, so the accept pipeline keys the
 * receipt, the conditional create, the attr patch, and compensation on the stamp
 * (design §O.4 R3). Nodes without an `applyOnAccept` are left unstamped, so an
 * accept with no registered family inserts the parsed content untouched.
 * `resolveContract` is injected for tests (defaults to the real registry).
 */
export function stampAcceptId(
  editor: Editor,
  html: string,
  suggestionId: string,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean,
  resolveContract: typeof contractForNode = contractForNode
): StampedContent {
  const nodes = suggestionInsertNodes(editor.schema, html)
  const nodeIds: string[] = []
  const visit = (node: JSONContent): void => {
    const attrs: JsonObject = node.attrs ?? {}
    if (
      node.type &&
      resolveContract(
        node.type,
        attrs,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )?.applyOnAccept
    ) {
      const nodeId = stampNodeId(suggestionId, nodeIds.length)
      node.attrs = { ...attrs, acceptId: nodeId }
      nodeIds.push(nodeId)
    }
    node.content?.forEach(visit)
  }
  nodes.forEach(visit)
  return { content: nodes, nodeIds }
}

/*
 * Tag for the completion patch's transaction. The note's autosave queues only
 * on a keystroke-adjacent doc change (CollaborativeEditor's onUpdate), and this
 * patch lands whole FHIR round trips after the click that accepted — so nothing
 * queues a save for it, and the save the accept's own insert already queued
 * carries content snapshotted BEFORE the side effect resolved (lodash debounce
 * invokes with the arguments of the call that scheduled it). The versioned
 * reference then reaches the collaborative room doc but never the Composition,
 * whose stale unversioned chip a reload pushes straight back into the room.
 * Tagging THIS transaction rather than saving on every doc change is what keeps
 * a remote peer's copy of the same change — which carries no meta — from making
 * every connected client write the note.
 */
const ACCEPT_COMPLETION_META = 'aiAcceptCompletion'

/*
 * Does this transaction carry an accept's completion patch? Read by the note
 * host to queue the content save that makes the frozen versioned reference
 * durable.
 */
export function isAcceptCompletionPatch(transaction: Transaction): boolean {
  return transaction.getMeta(ACCEPT_COMPLETION_META) === true
}

/*
 * Tag for an accept's content insert. The note's autosave otherwise queues only
 * on a keystroke-adjacent doc change, and an "Accept all" produces no keystrokes:
 * its inserts run `.chain().focus()...`, and TipTap's `focus` command
 * early-returns once the view already has focus, so only the FIRST accept of a
 * sweep fires the DOM focus event the autosave was leaning on. Accepts two
 * onward then changed the document with nothing queueing a save for them — the
 * clinician saw every edit and the server kept only the first. Tagging THIS
 * transaction, rather than saving on every doc change, is what keeps a remote
 * peer's copy of the same change — which carries no meta — from making every
 * connected client write the note.
 */
const ACCEPT_INSERT_META = 'aiAcceptInsert'

// Mark an accept's insert transaction. Called from the accept pipeline's insert
// chain so the whole insert (content + decoration drop) carries the tag.
export function markAcceptInsert(transaction: Transaction): void {
  transaction.setMeta(ACCEPT_INSERT_META, true)
}

/*
 * Did this transaction insert an accept's content? Read by the note host to
 * queue the content save that makes the accept durable.
 */
export function isAcceptInsert(transaction: Transaction): boolean {
  return transaction.getMeta(ACCEPT_INSERT_META) === true
}

/*
 * Patch a stamped node's attrs (e.g. set `reference`, clear draft state after
 * `applyOnAccept` links a resource), located by stamp id — NOT position, which a
 * concurrent collab edit moves across the applyOnAccept await. No-op when the
 * stamp is gone (content undone/removed during the side effect); the caller then
 * self-voids the orphaned resource (R4/H7a).
 */
export function updateAttrsByStamp(
  editor: Editor,
  nodeId: string,
  patch: JsonObject
): boolean {
  const matches: { pos: number; attrs: JsonObject }[] = []
  editor.state.doc.descendants((node, pos) => {
    if (matches.length === 0 && node.attrs?.acceptId === nodeId) {
      matches.push({ pos, attrs: node.attrs })
    }
    return matches.length === 0
  })
  const target = matches[0]
  if (!target) {
    return false
  }
  return editor
    .chain()
    .command(({ tr }) => {
      tr.setNodeMarkup(target.pos, undefined, { ...target.attrs, ...patch })
      // The completion-marker patch (e.g. the versioned reference) lands after
      // the async side effect — well past the collab UndoManager's ~500ms
      // capture window — so without this it is captured as its OWN undo item: a
      // single Cmd+Z would pop only the versioning and leave a stamped,
      // still-linked mention on an UNVERSIONED reference that the sign gate then
      // blocks with nothing highlighted to resolve. Exclude the patch from
      // history so undo removes the whole accept, never just the completion.
      tr.setMeta('addToHistory', false)
      tr.setMeta(ACCEPT_COMPLETION_META, true)
      return true
    })
    .run()
}
